import React, {useContext, useEffect, useState } from 'react';
import "react-datepicker/dist/react-datepicker.css";
import { ThemeContext } from "../../../../context/ThemeContext";
import TrendLineChart from './elements/TrendLineChart';
import { useGetAdminDashboardQuery, useGetLineChartDataQuery } from '../../../../store/services/admin/UserService';
import SessionSection from '../../../components/Dashboard/SessionSection';
import Pagination from '../../../components/Dashboard/Pagination';
import { Table } from '../../../components/Sessions/Table';
import { useSelector } from 'react-redux';
import { RevenueIcon, SessionIcon, StudentIcon } from '../../../../assets/icons/icons';

const AdminDashboard = () => {
	const [totalCount, setTotalCount] = useState({ upcoming: 0, past: 0, 
		new: 0, cancel: 0, absent: 0});
    const { currentUser } = useSelector((state) => state.auth);

	const [cardBlog, setCardBlog] = useState([]); 
	const [pagination, setPagination] = useState({
  		upcoming: { page: 1, search: "" },
  		past: { page: 1, search: "" },
  		new: { page: 1, search: "" },
  		cancel: { page: 1, search: "" },
  		absent: { page: 1, search: "" },
	});

	const { changeBackground } = useContext(ThemeContext);
	useEffect(() => {
		changeBackground({ value: "light", label: "Light" });
	}, []);

	const { data: upcomingData, isLoading: upcomingLoading } =useGetAdminDashboardQuery({
      	pageNumber: pagination.upcoming.page, search: pagination.upcoming.search, filter: "upcoming"});

  	const { data: pastData, isLoading: pastLoading } = useGetAdminDashboardQuery({
    	pageNumber: pagination.past.page, search: pagination.past.search, filter: "past"});

  	const { data: newBookingsData, isLoading: newBookingsLoading } = useGetAdminDashboardQuery({
      	pageNumber: pagination.new.page, search: pagination.new.search, filter: "newbookings"});

  	const { data: cancelledData, isLoading: cancelledLoading } = useGetAdminDashboardQuery({
      	pageNumber: pagination.cancel.page, search: pagination.cancel.search, filter: "cancelled"});

  	const { data: absentTutorsData, isLoading: absentTutorsLoading } = useGetAdminDashboardQuery({
      	pageNumber: pagination.absent.page, search: pagination.absent.search, filter: "absenttutors"});

	const { data: chartData, isLoading: chartLoading, error: chartError } = useGetLineChartDataQuery({
  		pageNumber: 1, pageSize: 10});

	useEffect(() => {
		setTotalCount({
			upcoming: upcomingData?.data.upcomingSession?.length || 0,
			past: pastData?.data.pastSessions?.length || 0,
			new: newBookingsData?.data.newBookings?.length || 0,
			cancel: cancelledData?.data.cancelledSession?.length || 0,
			absent: absentTutorsData?.data.absentTutors?.length || 0
		});
		if (upcomingData?.data) {
			setCardBlog([
				{ title: 'Students', svg: StudentIcon, number: upcomingData?.data.totalRegisteredUsersCount || 0, change: 'std-data' },
				{ title: 'Booked Sessions', svg: SessionIcon, number: upcomingData?.data.bookingsCount, change: 'event-data' },
  				{ title: 'Revenue', svg: RevenueIcon, number: upcomingData?.data.revenue, change: 'food-data bg-dark' },
			])
		}
	}, [upcomingData, pastData, newBookingsData, cancelledData, absentTutorsData]);

	const tutorColumns = [
		{ header: 'Tutor Name', accessor: 'tutorName' },
		{ header: 'Student Name', accessor: 'studentName' },
		{ header: 'Email', accessor: 'email' },
		{ header: 'Subject', accessor: 'subject' },
		{ header: 'Scheduled Date', accessor: 'scheduledDate',
      		render: (session) => new Date(session.scheduledDate).toLocaleDateString()
    	},
		{ header: 'Scheduled Time', accessor: 'scheduledTime' },
		{ 
		  header: 'Status', 
		  accessor: 'isActive',
		  render: (item) => (
			<span
			  className={`font-w600 text-${
				item.isActive ? "success" : "danger"}`} >
			  	{item.isActive ? "Active" : "Inactive"}
			</span>
		  ),
		},
	];

	if ( upcomingLoading || pastLoading || newBookingsLoading || cancelledLoading || absentTutorsLoading || chartLoading) 
		return <div className="text-center py-5"><div className="spinner-border" role="status"></div></div>;
  	if (chartError) return <div className="alert alert-danger">Error loading admin details.</div>;

	return (
		<>
			<div className="row">
				<div className="col-xl-6 mb-2">
					<h2 className="mb-2">Welcome back, {currentUser?.fullName}! 👋</h2>
					<p className="text-muted">Here’s a snapshot of your progress and upcoming lessons.</p>
				</div>
			</div>

			{/* Dashboard Cards */}
			<div className="row">
				<div className="col-xl-12">
					<div className="card">
						<div className="card-body pb-xl-4 pb-sm-3 pb-0">	
							<div className="row">
								{cardBlog.map((item, ind)=>(
									<div className="col-xl-4 col-sm-6 col-12" key={ind}>
										<div className="content-box">
											<div className={`icon-box icon-box-xl ${item.change}`}>
												{item.svg}
											</div>
											<div  className="chart-num">
												<p>{item.title}</p>
												<h2 className="font-w700 mb-0">{item.number}</h2>
											</div>
										</div>
									</div>
								))}			
							</div>	
						</div>
					</div>
				</div>
			</div>	

      		<div className="row">
				<div className="col-xl-6 ">
          			<TrendLineChart title="Bookings Over Time" data={chartData?.data?.bookingsChart}
               			xKey="date" yKey="value" yLabel="Bookings" />
        		</div>

        		<div className="col-xl-6 ">
          			<TrendLineChart title="Revenue Over Time" data={chartData?.data?.revenueChart}
            			xKey="date" yKey="value" yLabel="Revenue ($)" />
				</div>
			</div>
			
			{/* Upcoming Sessions */}
			<SessionSection title="Upcoming Lessons" data={upcomingData?.data?.upcomingSession || []}
  				totalCount={totalCount.upcoming} searchPlaceholder="Search upcoming sessions..." 
				searchTerm={pagination.upcoming.search} setSearchTerm={(val) => 
				setPagination(prev => ({...prev, upcoming: {...prev.upcoming, search: val, page: 1}}))} 
				setCurrentPage={(page) => setPagination(prev => ({...prev, upcoming: {...prev.upcoming, page}}))}
				currentPage={pagination.upcoming.page} pageSize={upcomingData?.data?.pageSize} showStudentName={true} showLanguage={true} showTutor={true}/>
			
      		<div className="row">
				<div className="col-xl-12 ">
          			<TrendLineChart title="User Sign-ups Over Time" data={chartData?.data?.signupsChart} xKey="date"
            			yKey="value" yLabel="Users" />
        		</div>
      		</div>

      		{/* Last 7 Sessions */}
			<SessionSection title="Recent Lessons" data={pastData?.data?.pastSessions || []}
  				totalCount={totalCount.past} searchTerm={pagination.past.search} setSearchTerm={(val) => 
				setPagination(prev => ({...prev, past: {...prev.past, search: val, page: 1}}))} 
				setCurrentPage={(page) => setPagination(prev => ({...prev, past: {...prev.past, page}}))}
				currentPage={pagination.past.page} pageSize={pastData?.data?.pageSize} showStudentName={true} showLanguage={true} showTutor={true}/>
		
      		{/* New Bookings */}
	  		<SessionSection title="New Bookings" data={newBookingsData?.data?.newBookings || []}
  			   	totalCount={totalCount.new} searchTerm={pagination.new.search} setSearchTerm={(val) => 
				setPagination(prev => ({...prev, new: {...prev.new, search: val, page: 1}}))} 
				setCurrentPage={(page) => setPagination(prev => ({...prev, new: {...prev.new, page}}))}
				currentPage={pagination.new.page} pageSize={newBookingsData?.data?.pageSize} showStudentName={true} showLanguage={true} showTutor={true}/>

			{/* Cancelled Sessions */}
	  		<SessionSection title="Cancelled Sessions" data={cancelledData?.data?.cancelledSession || []}
  			   	totalCount={totalCount.cancel} searchTerm={pagination.cancel.search} setSearchTerm={(val) => 
				setPagination(prev => ({...prev, cancel: {...prev.cancel, search: val, page: 1}}))} 
				setCurrentPage={(page) => setPagination(prev => ({...prev, cancel: {...prev.cancel, page}}))}
				currentPage={pagination.cancel.page} pageSize={cancelledData?.data?.pageSize} showStudentName={true} showLanguage={true} showTutor={true}/>

			<div className="row wow fadeInUp" data-wow-delay="1.5s">
            	<div className="col-12">
					<div className="card mt-0">
                    	<div className="card-header py-3 px-3 d-flex justify-content-between align-items-center">
                        	<h5 className="heading">Absent Tutors</h5>
                    	</div>
                    	<div className="card-body p-0 ">
                        	<div className="bg-white">
				            	<div className="page-title flex-wrap mb-0">
        			            	<div className="input-group search-area mb-md-0 ml-3">
          				            	<input type="text" className="form-control ps-4" placeholder="Search here..."
            				            	value={pagination.absent.search} onChange={(e) =>setPagination((prev) => ({
                    						...prev, absent: { ...prev.absent, search: e.target.value, page: 1 }}))} />
        			            	</div>
      			            	</div>
                
                                <Table data={absentTutorsData?.data?.absentTutors || []} columns={tutorColumns} pageSize={absentTutorsData?.data?.pageSize}
                                    filterConfig={[]} buttonConfig={[]} searchableColumns={[]} />

				            	<Pagination currentPage={pagination.absent.page} totalCount={totalCount.absent}
                                	pageSize={absentTutorsData?.data?.pageSize} onPageChange={(page) =>setPagination((prev) => ({
                					...prev, absent: { ...prev.absent, page }}))} />
                        	</div>
                    	</div>
                	</div>
            	</div>
       		</div>
		</>
	);
};

export default AdminDashboard;
